import { useBrand } from '@/config/brands';
import { type FraudCategory } from '@/config/fraudCategories';
import { type NationalBenchmark, type HospitalCodePricing } from '@/hooks/useFraudCategoryPricing';
import { markupBadgeColor } from '@/lib/grades';

// Patriotic palette
const NAVY = '#002868';
const GOLD = '#C5A55A';
const CREAM = '#FAF9F6';

interface CategoryCodeTableProps {
  category: FraudCategory;
  benchmarks: NationalBenchmark[];
  hospitalPricingMap: Map<string, HospitalCodePricing[]>;
  totalHospitals: number;
}

const fmt = (v: number | null) => {
  if (v == null || isNaN(v)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v);
};

const fmtPct = (v: number | null) => {
  if (v == null || isNaN(v)) return '—';
  const sign = v >= 0 ? '+' : '';
  if (Math.abs(v) >= 1000) return `${sign}${(v / 1000).toFixed(1)}k%`;
  return `${sign}${v.toFixed(1)}%`;
};

export const CategoryCodeTable = ({
  category,
  benchmarks,
  hospitalPricingMap,
  totalHospitals,
}: CategoryCodeTableProps) => {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';

  // Build state-level averages per code from hospital analytics
  const stateAvgByCode = new Map<string, { avg: number; count: number }>();
  for (const [, pricing] of hospitalPricingMap) {
    for (const hp of pricing) {
      const existing = stateAvgByCode.get(hp.procedureCode) || { avg: 0, count: 0 };
      existing.avg += hp.hospitalGrossCharge;
      existing.count += 1;
      stateAvgByCode.set(hp.procedureCode, existing);
    }
  }

  // Compute averages
  const stateAvgFinal = new Map<string, number>();
  for (const [code, { avg, count }] of stateAvgByCode) {
    stateAvgFinal.set(code, avg / count);
  }

  // Build rows: one per category code, enriched with benchmarks + state avg
  const rows = category.codes.map((catCode) => {
    const benchmark = benchmarks.find(
      (b) => b.code === catCode.code && b.codeType === catCode.codeType
    );
    const stateAvg = stateAvgFinal.get(
      catCode.codeType === 'DRG' ? `MS-${catCode.code}` : catCode.code
    ) ?? stateAvgFinal.get(catCode.code);

    const markup = (benchmark?.nationalAvg && stateAvg)
      ? ((stateAvg - benchmark.nationalAvg) / benchmark.nationalAvg) * 100
      : null;

    // Count hospitals with data for this code
    let hospCount = 0;
    for (const [, pricing] of hospitalPricingMap) {
      const codeToMatch = catCode.codeType === 'DRG' ? `MS-${catCode.code}` : catCode.code;
      if (pricing.some((p) => p.procedureCode === codeToMatch || p.procedureCode === catCode.code)) {
        hospCount++;
      }
    }

    return {
      code: catCode.code,
      codeType: catCode.codeType,
      description: catCode.description,
      nationalAvg: benchmark?.nationalAvg ?? null,
      nationalHospitals: benchmark?.totalHospitals ?? 0,
      stateAvg: stateAvg ?? null,
      markup,
      hospCount,
    };
  });

  // Sort by markup descending (worst first), nulls last
  rows.sort((a, b) => {
    if (a.markup == null && b.markup == null) return 0;
    if (a.markup == null) return 1;
    if (b.markup == null) return -1;
    return b.markup - a.markup;
  });

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr
            style={isAhp ? {
              background: `linear-gradient(135deg, ${NAVY} 0%, #001a4a 100%)`,
            } : undefined}
            className={!isAhp ? 'border-b bg-muted/50' : ''}
          >
            {['Code', 'Type', 'Description', 'National Avg', 'State Avg', 'Markup %', 'Hospitals'].map((label, i) => (
              <th
                key={label}
                className={`p-2.5 font-semibold text-xs tracking-wide uppercase ${
                  i >= 3 ? (i === 6 ? 'text-center' : 'text-right') : 'text-left'
                }`}
                style={isAhp ? { color: CREAM } : undefined}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={7}
                className="text-center py-8"
                style={{ color: isAhp ? 'rgba(27,27,27,0.35)' : undefined }}
              >
                No pricing data available for these codes
              </td>
            </tr>
          ) : (
            rows.map((row, idx) => (
              <tr
                key={`${row.codeType}-${row.code}`}
                style={isAhp ? {
                  borderBottom: '1px solid rgba(0,0,0,0.06)',
                  backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(0,40,104,0.02)',
                } : undefined}
                className={!isAhp ? 'border-b hover:bg-muted/30' : ''}
              >
                <td
                  className="p-2.5 font-mono text-xs"
                  style={isAhp ? { color: NAVY, fontWeight: 600 } : undefined}
                >
                  {row.code}
                </td>
                <td className="p-2.5 text-xs">
                  <span
                    className="px-1.5 py-0.5 rounded text-xs font-semibold"
                    style={isAhp ? {
                      backgroundColor: `${NAVY}10`,
                      color: NAVY,
                      border: `1px solid ${NAVY}20`,
                    } : {
                      backgroundColor: `${category.color}15`,
                      color: category.color,
                    }}
                  >
                    {row.codeType}
                  </span>
                </td>
                <td
                  className="p-2.5 text-xs max-w-xs truncate"
                  title={row.description}
                  style={isAhp ? { color: 'rgba(27,27,27,0.7)' } : undefined}
                >
                  {row.description}
                </td>
                <td
                  className="p-2.5 text-right font-mono text-xs"
                  style={isAhp ? { color: 'rgba(27,27,27,0.6)' } : undefined}
                >
                  {fmt(row.nationalAvg)}
                </td>
                <td
                  className="p-2.5 text-right font-mono text-xs"
                  style={isAhp ? { color: 'rgba(27,27,27,0.6)' } : undefined}
                >
                  {fmt(row.stateAvg)}
                </td>
                <td className="p-2.5 text-right font-mono text-xs font-bold">
                  {row.markup != null ? (
                    <span style={{ color: markupBadgeColor(row.markup) }}>
                      {fmtPct(row.markup)}
                    </span>
                  ) : (
                    <span style={{ color: isAhp ? 'rgba(27,27,27,0.25)' : undefined }} className={!isAhp ? 'text-muted-foreground' : ''}>—</span>
                  )}
                </td>
                <td
                  className="p-2.5 text-center font-mono text-xs"
                  style={isAhp ? {
                    color: row.hospCount > 0 ? NAVY : 'rgba(27,27,27,0.25)',
                    fontWeight: row.hospCount > 0 ? 600 : 400,
                  } : undefined}
                >
                  {row.hospCount > 0 ? row.hospCount : '—'}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
